import type { Chain } from 'viem';
import { useChains } from 'wagmi';

import { ChainSlug, Currency, ERC20_TOKENS } from '@/constants';
import {
  isBtcCurrency,
  isErc20Currency,
  isEvmCurrency,
  decimalsForCurrency,
  isTronCurrency,
  isTrc20Currency,
  isLtcCurrency,
  isDogeCurrency,
  isCwebCurrency,
} from '@/utils';

import useChainIconByCurrency from './useChainIconByCurrency';

export type ChainData = {
  chainSlug?: ChainSlug;
  isBtc: boolean;
  isLtc: boolean;
  isDoge: boolean;
  isEvm: boolean;
  isERC20: boolean;
  isNative: boolean;
  isTron: boolean;
  isCweb: boolean;
  isPol: boolean;
  isL2: boolean;
  chainData: Chain | Partial<Chain> | null;
};

const DEFAULT_CHAIN_DATA: ChainData = {
  isNative: false,
  isBtc: false,
  isLtc: false,
  isDoge: false,
  isEvm: false,
  isERC20: false,
  isTron: false,
  isCweb: false,
  isPol: false,
  isL2: false,
  chainData: null,
};

const useChainByCurrency = (currency?: Currency) => {
  const evmWagmiChains = useChains();
  const { getChainIcon } = useChainIconByCurrency(currency);

  const collectChainData = (currency: Currency): ChainData => {
    const [token, tokenChain] = currency.split('_');

    if (isBtcCurrency(currency)) {
      return {
        ...DEFAULT_CHAIN_DATA,
        chainSlug: ChainSlug.BTC,
        isNative: true,
        isBtc: true,
        chainData: {
          name: 'Bitcoin',
          nativeCurrency: { name: 'Bitcoin', symbol: Currency.BTC, decimals: decimalsForCurrency(Currency.BTC) },
          custom: { icon: getChainIcon(Currency.BTC) },
        } as Partial<Chain>,
      };
    }

    if (isLtcCurrency(currency)) {
      return {
        ...DEFAULT_CHAIN_DATA,
        chainSlug: ChainSlug.LTC,
        isNative: true,
        isLtc: true,
        chainData: {
          name: 'Litecoin',
          nativeCurrency: { name: 'Litecoin', symbol: Currency.LTC, decimals: decimalsForCurrency(Currency.LTC) },
          custom: { icon: getChainIcon(Currency.LTC) },
        } as Partial<Chain>,
      };
    }
    if (isDogeCurrency(currency)) {
      return {
        ...DEFAULT_CHAIN_DATA,
        chainSlug: ChainSlug.DOGE,
        isNative: true,
        isDoge: true,
        chainData: {
          name: 'Dogecoin',
          nativeCurrency: { name: 'Dogecoin', symbol: Currency.DOGE, decimals: decimalsForCurrency(Currency.DOGE) },
          custom: { icon: getChainIcon(Currency.DOGE) },
        } as Partial<Chain>,
      };
    }

    if (isErc20Currency(currency)) {
      const isERC20 = ERC20_TOKENS.some((c) => c === currency);
      const chainData = evmWagmiChains.find(
        (chain) => chain.nativeCurrency.symbol.toUpperCase() === (tokenChain || token).toUpperCase()
      );

      return {
        ...DEFAULT_CHAIN_DATA,
        chainSlug: ChainSlug.EVM,
        isEvm: true,
        isERC20,
        chainData: chainData || null,
      };
    }

    if (isEvmCurrency(currency)) {
      const isNative = !tokenChain;

      const chainData = evmWagmiChains.find(
        (chain) => chain.nativeCurrency.symbol.toUpperCase() === (tokenChain || token).toUpperCase()
      );

      return {
        ...DEFAULT_CHAIN_DATA,
        chainSlug: ChainSlug.EVM,
        isEvm: true,
        isNative,
        chainData: chainData || null,
      };
    }

    if (isTronCurrency(currency)) {
      const isNative = !tokenChain;

      return {
        ...DEFAULT_CHAIN_DATA,
        chainSlug: ChainSlug.TRON,
        isTron: true,
        isNative,
        chainData: {
          name: 'Tron',
          nativeCurrency: { name: 'Tron', symbol: Currency.TRX, decimals: decimalsForCurrency(Currency.TRX) },
          custom: { icon: getChainIcon(Currency.TRX) },
        } as Partial<Chain>,
      };
    }

    if (isTrc20Currency(currency)) {
      return {
        ...DEFAULT_CHAIN_DATA,
        chainSlug: ChainSlug.TRON,
        isTron: true,
        isNative: false,
        chainData: {
          name: 'Tron',
          nativeCurrency: { name: 'Tron', symbol: Currency.TRX, decimals: decimalsForCurrency(Currency.TRX) },
          custom: { icon: getChainIcon(Currency.TRX) },
        } as Partial<Chain>,
      };
    }

    if (isCwebCurrency(currency)) {
      return {
        ...DEFAULT_CHAIN_DATA,
        chainSlug: ChainSlug.CWEB,
        isCweb: true,
        isL2: true,
        isNative: true,
        chainData: {
          name: 'Coinweb',
          nativeCurrency: { name: 'Coinweb', symbol: Currency.CWEB, decimals: decimalsForCurrency(Currency.CWEB) },
          custom: { icon: getChainIcon(Currency.CWEB) },
        } as Partial<Chain>,
      };
    }

    return DEFAULT_CHAIN_DATA;
  };

  return {
    ...(currency && collectChainData(currency)),
    findChainByCurrency: collectChainData,
  };
};

export default useChainByCurrency;
